import { Component, OnInit, ChangeDetectionStrategy, DestroyRef, inject, ViewChild, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';

import { SendForSignaturePage } from 'src/pages/compliance/send-for-signature/send-for-signature';

import { Agreement, AgreementSigner } from 'src/models/agreement.model';

@Component({
  templateUrl: 'agreement-detail.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AgreementDetailPage implements OnInit {
  private destroyRef = inject(DestroyRef);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private fb = inject(FormBuilder);
  private util = inject(Util);

  readonly windowService = inject(WindowService);

  form: UntypedFormGroup;
  isRightSideContent = true;
  dialogParams: any;
  agreement: Agreement;
  headers: string[] = ['Name', 'Email', 'Status', 'Sent Date', 'Signed Date'];
  allSignerRows: any[] = [];
  shownSignerRows: any[] = [];
  loaded = false;
  errorMessage: string;
  @ViewChild('signersTable') signersTable: any;
  private panelClosedSubscription: Subscription;

  private mockSigners: AgreementSigner[] = [
    { id: 1, agreement_id: 1, name: 'Sarah Johnson', email: 'sarah.johnson@email.com', status: 'signed', sent_at: '2026-01-15', signed_at: '2026-01-16' },
    { id: 2, agreement_id: 1, name: 'Michael Chen', email: 'michael.chen@email.com', status: 'signed', sent_at: '2026-01-18', signed_at: '2026-01-19' },
    { id: 3, agreement_id: 1, name: 'Emily Davis', email: 'emily.davis@email.com', status: 'pending', sent_at: '2026-02-10' },
    { id: 4, agreement_id: 1, name: 'James Wilson', email: 'james.wilson@email.com', status: 'declined', sent_at: '2026-02-05' },
    { id: 5, agreement_id: 1, name: 'Maria Garcia', email: 'maria.garcia@email.com', status: 'expired', sent_at: '2025-11-01' },
  ];

  constructor() {
    this.form = this.fb.group({
      search: [''],
    });
    this.form.get('search')?.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => this.updateSearch(value));
  }

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.dialogParams = await this.storage.retrieve('dialog-params');
    this.agreement = this.dialogParams?.agreement || {
      id: 0,
      name: 'Agreement',
      created_at: '',
      updated_at: '',
      status: 'active',
    };
    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogPageTitle(this.agreement.name);
    }
    this.loadPage();
  }

  loadPage() {
    const rows = this.buildSignerRows(this.mockSigners);
    this.allSignerRows = rows;
    this.shownSignerRows = rows;
    this.loaded = true;
  }

  buildSignerRows(signers: AgreementSigner[]): any[] {
    return signers.map(signer => ({
      'Name': { value: signer.name },
      'Email': { value: signer.email },
      'Status': { value: this.getStatusLabel(signer.status), color: this.getStatusColor(signer.status) },
      'Sent Date': { value: signer.sent_at },
      'Signed Date': { value: signer.signed_at || '—' },
      id: signer.id,
    }));
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'signed': return 'Signed';
      case 'pending': return 'Pending';
      case 'declined': return 'Declined';
      case 'expired': return 'Expired';
      default: return status;
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'signed': return '#2E7D32';
      case 'pending': return '#F9A825';
      case 'declined': return '#C62828';
      case 'expired': return '#C62828';
      default: return '#1E2125';
    }
  }

  updateSearch(search: string) {
    if (!search || search.length < 2) {
      this.shownSignerRows = [...this.allSignerRows];
      return;
    }
    const term = search.toLowerCase();
    this.shownSignerRows = this.allSignerRows.filter(row =>
      row['Name'].value.toLowerCase().includes(term) ||
      row['Email'].value.toLowerCase().includes(term)
    );
  }

  goToSendForSignature() {
    const params = { agreement: this.agreement };
    this.goToPage('send-for-signature', params, SendForSignaturePage);
  }

  async goToPage(url: string, params: any, component: any) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.loadPage();
      });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
